import { Field, useFormikContext } from 'formik';
import { OptionProps, components, MultiValueGenericProps } from 'react-select';
import { AlertTriangle } from 'lucide-react';

import { useGroups } from '@/react/portainer/environments/environment-groups/queries';
import { useCreateGroupMutation } from '@/react/portainer/environments/environment-groups/queries/useCreateGroupMutation';
import { EnvironmentGroupId } from '@/react/portainer/environments/types';
import { Badge } from '@/react/components/Badge';
import { notifySuccess } from '@/portainer/services/notifications';

import { FormControl } from '@@/form-components/FormControl';
import { Input } from '@@/form-components/Input';
import { FormSection } from '@@/form-components/FormSection';
import { Option, PortainerSelect } from '@@/form-components/PortainerSelect';
import { TextTip } from '@@/Tip/TextTip';
import { Link } from '@@/Link';

import { EnvironmentGroup } from '../../environments/environment-groups/types';
import { PolicyType, TypedPolicy } from '../types';

/**
 * Form values that PolicyConfigurationFormSection reads/writes via Formik context.
 * All policy forms must include these fields in their Formik values.
 */
export interface PolicyConfigFormValues {
  name: string;
  environmentGroupIds: Array<EnvironmentGroupId>;
}

/**
 * @deprecated This type is deprecated and will be removed in a future version.
 * Use PolicyConfigFormValues instead.
 *
 * Legacy type used by ConfigTab controlled mode (to be migrated in Phase 4).
 */
export interface PolicyConfigurationValues {
  name: string;
  type: PolicyType;
  environmentGroupIds: Array<EnvironmentGroupId>;
}

/**
 * Extended option type that includes conflict metadata for environment groups
 */
export interface EnvironmentGroupOption extends Option<number> {
  hasConflict: boolean;
  conflictingPolicyName?: string;
}

interface Props {
  /** Policy type - used for filtering environment groups and detecting conflicts */
  policyType: PolicyType;
  /** Whether the name field should be read-only (for edit mode) */
  nameReadOnly?: boolean;
  hideNameField?: boolean;
  hideEnvironmentGroupsField?: boolean;
}

/**
 * Policy Configuration Form Section
 *
 * Renders the common configuration fields for all policy types:
 * - Name input
 * - Environment groups multi-select with conflict detection
 *
 * This component must be rendered inside a Formik context as it uses
 * useFormikContext to read/write form values.
 */
export function PolicyConfigurationFormSection({
  policyType,
  nameReadOnly = false,
  hideNameField = false,
  hideEnvironmentGroupsField = false,
}: Props) {
  const { values, errors, setFieldValue } =
    useFormikContext<PolicyConfigFormValues>();

  const groupsOptionsQuery = useGroups({
    select: (environmentGroups) =>
      getFilteredEnvironmentGroupOptions(policyType, environmentGroups),
  });
  const groupOptions = groupsOptionsQuery.data || [];
  const createGroupMutation = useCreateGroupMutation();

  if (hideNameField && hideEnvironmentGroupsField) {
    return null;
  }

  return (
    <FormSection title="Policy configuration">
      {!hideNameField && (
        <FormControl label="Name" required errors={errors.name}>
          <Field
            as={Input}
            name="name"
            placeholder="e.g. my-policy"
            readOnly={nameReadOnly}
            data-cy="policy-name-input"
          />
        </FormControl>
      )}

      {!hideEnvironmentGroupsField && (
        <FormControl
          label="Environment groups"
          tooltip="Select one or more environment groups to apply this policy to"
          required
          errors={errors.environmentGroupIds}
        >
          {groupOptions.length === 0 && !groupsOptionsQuery.isLoading && (
            <TextTip color="blue" inline={false} className="mb-2">
              No environment groups available. You can{' '}
              <Link
                to="portainer.groups.new"
                target="_blank"
                rel="noopener noreferrer"
                data-cy="create-environment-group-link"
              >
                create an environment group
              </Link>{' '}
              or type a name in the selector below to create one.
            </TextTip>
          )}
          <PortainerSelect<number>
            isMulti
            isCreatable
            onCreateOption={handleCreateGroup}
            formatCreateLabel={(input) => `Create group "${input}"`}
            isLoading={
              groupsOptionsQuery.isLoading || createGroupMutation.isLoading
            }
            value={values.environmentGroupIds}
            onChange={(newGroups) =>
              setFieldValue('environmentGroupIds', newGroups)
            }
            options={groupOptions}
            data-cy="policy-groups-select"
            noOptionsMessage={() => 'No environment groups available.'}
            components={{
              // Cast is needed because PortainerSelect expects Option<number> but we extend it
              // The actual options passed are EnvironmentGroupOption which have the extra fields
              Option:
                EnvironmentGroupOptionComponent as typeof components.Option,
              MultiValueLabel:
                EnvironmentGroupMultiValueLabel as typeof components.MultiValueLabel,
            }}
          />
        </FormControl>
      )}
    </FormSection>
  );

  function handleCreateGroup(name: string) {
    createGroupMutation.mutate(
      { name },
      {
        onSuccess: (data) => {
          setFieldValue('environmentGroupIds', [
            ...values.environmentGroupIds,
            data.Id,
          ]);
          notifySuccess('Group created', `Group "${data.Name}" created`);
        },
      }
    );
  }
}

/**
 * Custom Option component that displays conflict warnings for environment groups
 */
export function EnvironmentGroupOptionComponent({
  data,
  ...props
}: OptionProps<EnvironmentGroupOption, true>) {
  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <components.Option data={data} {...props}>
      <div className="flex items-center justify-between w-full">
        <span>{data.label}</span>
        {data.hasConflict && (
          <Badge type="warn" className="ml-2 whitespace-nowrap">
            Replaces: {data.conflictingPolicyName}
          </Badge>
        )}
      </div>
    </components.Option>
  );
}

/**
 * Custom MultiValueLabel component that displays a warning icon for conflicting selections
 */
export function EnvironmentGroupMultiValueLabel({
  data,
  ...props
}: MultiValueGenericProps<EnvironmentGroupOption, true>) {
  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <components.MultiValueLabel data={data} {...props}>
      <div className="flex items-center gap-1">
        <span>{data.label}</span>
        {data.hasConflict && (
          <AlertTriangle
            className="w-3 h-3 text-warning-7"
            aria-label={`Will replace policy: ${data.conflictingPolicyName}`}
          />
        )}
      </div>
    </components.MultiValueLabel>
  );
}

/**
 * Get the conflicting policy from an environment group if one exists.
 * Optionally excludes a specific policy ID from conflict detection (for edit mode).
 * @param group - The environment group to check
 * @param policyType - The policy type to check for conflicts
 * @param currentPolicyId - Optional ID of the current policy being edited (excluded from conflicts)
 */
export function getConflictingPolicy(
  group: EnvironmentGroup,
  policyType: PolicyType,
  currentPolicyId?: number
): TypedPolicy | undefined {
  return group.Policies?.find(
    (policy) => policy.Type === policyType && policy.Id !== currentPolicyId
  );
}

/**
 * Check if any of the selected environment groups have a conflicting policy of the given type
 */
export function hasSelectedGroupsWithConflicts(
  selectedGroupIds: Array<number>,
  allGroups: Array<EnvironmentGroup>,
  policyType: PolicyType
): boolean {
  return selectedGroupIds.some((groupId) => {
    const group = allGroups.find((g) => g.Id === groupId);
    if (!group) return false;
    return !!getConflictingPolicy(group, policyType);
  });
}

/**
 * Filter out the unassigned group, and map the groups as environment options
 * with policy conflict information.
 */
export function getFilteredEnvironmentGroupOptions(
  policyType: PolicyType,
  allGroups: Array<EnvironmentGroup>,
  currentPolicyId?: number
): Array<EnvironmentGroupOption> {
  const filteredGroups = allGroups.filter((group) => {
    // Exclude "Unassigned" groups
    if (group.Name.toLowerCase().includes('unassigned')) {
      return false;
    }

    return true;
  });

  return filteredGroups.map((group) => {
    const conflictingPolicy = getConflictingPolicy(
      group,
      policyType,
      currentPolicyId
    );
    return {
      value: group.Id,
      label: group.Name,
      hasConflict: !!conflictingPolicy,
      conflictingPolicyName: conflictingPolicy?.Name,
    };
  });
}
